import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { FormField } from '../FormField.jsx';
import { TextInput } from '../TextInput.jsx';
import { SubmitButton } from '../SubmitButton.jsx';

const TIER_LABELS = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Professional',
  enterprise: 'Enterprise',
};

const STATUS_LABELS = {
  active: 'Active',
  pending_payment: 'Pending payment',
  expiring_soon: 'Expiring soon',
  grace: 'Grace period',
  read_only: 'Read-only',
  expired: 'Expired',
};

export function CompanySection({ user }) {
  const isAdmin = user?.role === 'admin';
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [name, setName] = useState('');
  const [fieldError, setFieldError] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsCompany();
        if (cancelled) return;
        setCompany(data);
        setName(data.name || '');
      } catch (err) {
        if (cancelled) return;
        setLoadError(err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <section className="settings-section">
        <h2>Company</h2>
        <p className="settings-section-hint">Only organisation admins can view company settings.</p>
      </section>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError(null);
    setGlobalError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const trimmed = name.trim();
      const res = await api.patchSettingsCompany({ name: trimmed });
      setCompany((c) => ({ ...c, name: res.name }));
      setName(res.name);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors?.name) {
        setFieldError(err.fieldErrors.name);
      } else {
        setGlobalError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <section className="settings-section">
        <h2>Company</h2>
        <p className="invite-error" role="alert">Couldn&rsquo;t load company settings.</p>
      </section>
    );
  }

  if (!company) {
    return (
      <section className="settings-section">
        <h2>Company</h2>
        <p className="form-loading">Loading…</p>
      </section>
    );
  }

  const tierLabel = TIER_LABELS[company.subscription_tier] || company.subscription_tier;
  const statusLabel = STATUS_LABELS[company.subscription_status] || company.subscription_status;

  return (
    <section className="settings-section">
      <h2>Company</h2>
      <p className="settings-section-hint">Manage organisation-wide details.</p>

      <form onSubmit={handleSubmit} className="settings-form" noValidate>
        <FormField label="Company name" htmlFor="company-name" error={fieldError}>
          <TextInput
            id="company-name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={Boolean(fieldError)}
            maxLength={120}
            required
          />
        </FormField>

        <div className="settings-readonly-grid">
          <div>
            <div className="settings-readonly-label">Subscription tier</div>
            <div className="settings-readonly-value">{tierLabel}</div>
          </div>
          <div>
            <div className="settings-readonly-label">Status</div>
            <div className="settings-readonly-value">{statusLabel}</div>
          </div>
          <div>
            <div className="settings-readonly-label">Members</div>
            <div className="settings-readonly-value">{company.member_count}</div>
          </div>
        </div>

        <div className="settings-form-actions">
          <SubmitButton loading={saving} disabled={!name.trim()}>
            Save company
          </SubmitButton>
          {success && (
            <span className="invite-success" role="status">Company updated.</span>
          )}
          {globalError && (
            <span className="invite-error" role="alert">
              {globalError instanceof ApiError ? (globalError.message || 'Couldn\u2019t update company.') : 'Couldn\u2019t update company.'}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
